/* src/features/5DD/Journal.js */

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../apiClient';
import { format, startOfDay, endOfDay } from 'date-fns';
import AudioRecorder from './AudioRecorder';

// --- STYLES ---
const styles = {
  journalContainer: {
    display: 'flex', flexDirection: 'column', gap: '20px', 
    color: '#333', // Default text color
    paddingBottom: '40px'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
    color: '#333', 
    marginBottom: '10px'
  },
  backButton: {
    padding: '8px 12px', fontSize: '1em', background: '#333', border: 'none',
    color: '#fff', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit',
  },
  
  // --- NEW BUTTON STYLING (Dark Anchor) ---
  modeToggleContainer: {
    display: 'flex', 
    justifyContent: 'center', 
    gap: '12px', 
    marginBottom: '15px',
    width: '100%'
  },
  modeButton: {
    flex: 1,                    // Forces equal width
    padding: '14px 0',          // Taller touch target
    backgroundColor: '#1f1f1f', // Dark Charcoal (High contrast against light backgrounds)
    color: '#e0e0e0',           // Off-white text
    border: '1px solid #333',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1em',
    fontWeight: '500',
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
  },
  modeActive: {
    flex: 1,
    padding: '14px 0',
    backgroundColor: '#b08d6d', // Brand Gold
    color: '#fff',              // Pure White
    border: '1px solid #b08d6d',
    borderRadius: '8px',
    cursor: 'default',
    fontWeight: 'bold',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)' // Slight inner shadow for depth
  },
  // ----------------------------------------

  warningNote: {
    textAlign: 'center',
    color: '#d32f2f', 
    fontWeight: 'bold',
    fontSize: '0.9em',
    padding: '12px',
    background: '#fff0f0', // Light red background
    borderRadius: '8px',
    marginBottom: '15px',
    border: '1px solid #ffcdd2',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
  },

  prompt: {
    background: '#fff', 
    padding: '20px', borderRadius: '8px',
    border: '1px solid #ccc', color: '#333',
    boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
  },
  promptIntro: {
    fontSize: '1.1em', lineHeight: '1.5', marginBottom: '15px', fontStyle: 'italic',
  },
  actionItem: {
    background: '#f9f9f9', padding: '12px', borderRadius: '6px',
    marginBottom: '10px', fontSize: '0.95em', border: '1px solid #eee'
  },
  actionLabel: {
    fontWeight: 'bold', color: '#b08d6d', marginRight: '5px',
  },
  textarea: {
    width: '100%', height: '180px', padding: '15px', fontSize: '1em',
    borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box',
    fontFamily: 'inherit', background: '#fff', color: '#333', marginTop: '15px',
    lineHeight: '1.5'
  },
  saveButton: {
    padding: '14px', fontSize: '1.1em', fontWeight: 'bold', color: '#fff',
    backgroundColor: '#007bff', border: 'none', borderRadius: '6px', cursor: 'pointer',
    marginTop: '15px', width: '100%', fontFamily: 'inherit',
    boxShadow: '0 2px 5px rgba(0,123,255,0.3)'
  },
  entryList: {
    display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '30px'
  },
  entryItem: {
    background: '#fff', border: '1px solid #e0e0e0', padding: '15px 20px',
    borderRadius: '8px', color: '#333', boxShadow: '0 2px 5px rgba(0,0,0,0.03)'
  },
  entryItemTime: {
    display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', color: '#666', marginBottom: '8px', fontSize: '0.9em'
  },
  entryItemContent: {
    margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word', lineHeight: '1.6'
  },
  audioBadge: {
    color: '#b08d6d', fontSize: '0.9em', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600'
  }
};

function Journal({ session, day, dayIndex, navigateTo }) {
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState('');
  const [loading, setLoading] = useState(true);
  const [promptData, setPromptData] = useState(null);
  const [promptLoading, setPromptLoading] = useState(true);
  const [entryMode, setEntryMode] = useState('write'); // 'write' or 'speak'

  // Fetch entries for the current day
  const fetchDailyEntries = useCallback(async () => {
    setLoading(true);
    const startDate = startOfDay(day);
    const endDate = endOfDay(day);

    try {
      const startISO = startDate.toISOString();
      const endISO = endDate.toISOString();
      const data = await db.journal.list({ start_date: startISO, end_date: endISO });
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching journal entries:', error.message);
    } finally {
      setLoading(false);
    }
  }, [session.user.id, day]);

  // Fetch Dynamic Prompt
  const fetchDynamicPrompt = useCallback(async () => {
    setPromptLoading(true);
    try {
      let period = 'morning';
      if (dayIndex === 0) {
        period = 'start';
      } else {
        const currentHour = new Date().getHours();
        period = currentHour >= 12 ? 'evening' : 'morning';
      }

      const data = await db.prompts.list();
      const filtered = (data || []).filter(p => p.day_number === dayIndex && p.period === period);
      if (filtered.length > 0) {
        const randomIndex = Math.floor(Math.random() * filtered.length);
        setPromptData(filtered[randomIndex]);
      } else {
        setPromptData({
          intro_text: "Reflect on your day.",
          action_1: "What are you feeling?",
          action_2: "How are you feeling that?",
          action_3: "What is one thing you can do now?"
        });
      }

    } catch (error) {
      console.error('Error fetching prompt:', error.message);
    } finally {
      setPromptLoading(false);
    }
  }, [dayIndex]);

  useEffect(() => {
    fetchDailyEntries();
    fetchDynamicPrompt();
  }, [fetchDailyEntries, fetchDynamicPrompt]);

  const handleSaveEntry = async () => {
    if (newEntry.trim() === '') return; 

    try {
      const data = await db.journal.create({
        content: newEntry,
        prompt_key: promptData ? `Day ${dayIndex} ${promptData.period} - Var ${promptData.variation}` : 'Custom',
      });

      const taskDate = format(day, 'yyyy-MM-dd');
      await db.tasks.upsert({
        task_date: taskDate,
        completed_journal: true,
      });

      setNewEntry('');
      setEntries([data, ...entries]);
      alert('Journal entry saved!');
      
    } catch (error) {
      console.error('Error saving journal entry:', error.message);
    }
  };

  const handleAudioUploadComplete = () => {
      fetchDailyEntries(); 
      setEntryMode('write'); // Switch back to list view after recording
  };

  return (
    <div style={styles.journalContainer}>
      <div style={styles.header}>
        <h3>Journal for Day {dayIndex}</h3>
        <button style={styles.backButton} onClick={() => navigateTo('dashboard')}>
          &larr; Back to Day
        </button>
      </div>

      {/* --- BUTTONS: SYMMETRICAL TOGGLE BAR --- */}
      <div style={styles.modeToggleContainer}>
        <button 
          style={entryMode === 'write' ? styles.modeActive : styles.modeButton}
          onClick={() => setEntryMode('write')}
        >
          Written Journal
        </button>
        <button 
          style={entryMode === 'speak' ? styles.modeActive : styles.modeButton}
          onClick={() => setEntryMode('speak')}
        >
          Audio Journal
        </button>
      </div>
      {/* --------------------------------------- */}

      {/* --- MODE: WRITE --- */}
      {entryMode === 'write' ? (
          <div style={styles.prompt}>
            {promptLoading ? (
              <p>Loading prompt...</p>
            ) : promptData ? (
              <>
                <p style={styles.promptIntro}>{promptData.intro_text}</p>
                <div style={styles.actionItem}>
                  <span style={styles.actionLabel}>Action 1:</span>{promptData.action_1}
                </div>
                <div style={styles.actionItem}>
                  <span style={styles.actionLabel}>Action 2:</span>{promptData.action_2}
                </div>
                <div style={styles.actionItem}>
                  <span style={styles.actionLabel}>Action 3:</span>{promptData.action_3}
                </div>
              </>
            ) : (
              <p>Ready to journal?</p>
            )}

            <textarea
              style={styles.textarea}
              value={newEntry}
              onChange={(e) => setNewEntry(e.target.value)}
              placeholder="Write your thoughts here..."
            />

            <button style={styles.saveButton} onClick={handleSaveEntry}>
              Save Entry
            </button>
          </div>
      ) : (
          /* --- MODE: AUDIO --- */
          <div>
            <div style={styles.warningNote}>
              ⚠️ IMPORTANT: Do not navigate away or close this tab while the recorder is running or uploading.
            </div>
            
            <AudioRecorder 
              session={session} 
              onUploadComplete={handleAudioUploadComplete} 
            />
          </div>
      )}

      {/* --- ENTRIES LIST --- */}
      <div>
        <h4>Today's Entries</h4>
        {loading ? (
          <p>Loading entries...</p>
        ) : entries.length === 0 ? (
          <p>No entries for today yet.</p>
        ) : (
          <div style={styles.entryList}>
            {entries.map((entry) => (
              <div key={entry.id} style={styles.entryItem}>
                <div style={styles.entryItemTime}>
                   <span>{format(new Date(entry.created_at), 'h:mm a')}</span>
                   {entry.audio_path && (
                       <span style={styles.audioBadge}>🎙️ Audio Journal</span>
                   )}
                </div>
                
                <p style={styles.entryItemContent}>
                    {entry.content || (entry.is_transcribing ? <em>Transcribing audio...</em> : '')}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Journal;